import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import GenericFileUpload from '../../../../components/common/GenericFileUpload';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getTrainingDocumentFileName } from '../utils/documentFileName';

const PDF_TYPES = { 'application/pdf': ['.pdf'] };

const TrainingDocumentEditDialog = ({ visible, onHide, formik, loading, deleting, document, onDelete, onDownloadDocument, readOnly = false }) => {
    const { th } = useLanguage();
    const invalid = (field) => formik.touched[field] && Boolean(formik.errors[field]);
    const existingDocuments = (document?.documents ?? [])
        .map((item) => ({
            ...item,
            id: item.documentId,
            fileName: getTrainingDocumentFileName(item)
        }))
        .filter((item) => !formik.values.removedDocumentIds.includes(item.id));

    const footer = (
        <div>
            {onDelete && (
                <Button
                    type="button"
                    label={th('common.delete')}
                    icon="pi pi-trash"
                    severity="danger"
                    disabled={loading || deleting}
                    onClick={() =>
                        confirmDialog({
                            message: th('lmsTrainingDocument.confirmDelete'),
                            header: th('common.delete'),
                            icon: 'pi pi-exclamation-triangle',
                            acceptClassName: 'p-button-danger',
                            accept: () => onDelete(document)
                        })
                    }
                />
            )}
            <Button type="button" label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} disabled={loading} />
            {!readOnly && <Button type="button" label={th('common.save')} icon="pi pi-check" onClick={formik.submitForm} loading={loading} disabled={loading || deleting} />}
        </div>
    );

    return (
        <Dialog visible={visible} onHide={onHide} header={th('lmsTrainingDocument.editTitle')} footer={footer} modal className="p-fluid" style={{ width: '60rem', maxWidth: '95vw' }}>
            <div className="grid">
                <div className="field col-12 md:col-6">
                    <label htmlFor="editVehicleModel">{th('lmsTrainingDocument.vehicleModel')}</label>
                    <InputText id="editVehicleModel" value={document?.vehicleName ?? '-'} disabled />
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="editTrainingType">{th('lmsTrainingDocument.trainingType')}</label>
                    <InputText id="editTrainingType" value={document?.trainingName ?? '-'} disabled />
                </div>
            </div>

            <div className="field">
                <label htmlFor="editDescription">
                    {th('lmsTrainingDocument.description')} <span className="text-red-500">*</span>
                </label>
                <InputTextarea id="editDescription" name="description" value={formik.values.description} onChange={formik.handleChange} onBlur={formik.handleBlur} rows={4} autoResize className={invalid('description') ? 'p-invalid' : ''} />
                {invalid('description') && <small className="p-error">{formik.errors.description}</small>}
            </div>

            <div className="field">
                <label htmlFor="editIsPublic">{th('lmsTrainingDocument.visibility')}</label>
                <div className="flex align-items-center gap-2">
                    <InputSwitch inputId="editIsPublic" checked={formik.values.isPublic} onChange={(event) => formik.setFieldValue('isPublic', event.value)} disabled={loading} />
                    <span>{th(formik.values.isPublic ? 'lmsTrainingDocument.visibilityPublic' : 'lmsTrainingDocument.visibilityPrivate')}</span>
                </div>
                {document?.visibility === 'mixed' && <small className="text-color-secondary">{th('lmsTrainingDocument.visibilityMixedHint')}</small>}
            </div>

            <div className="field mb-0">
                <label>{th('lmsTrainingDocument.file')}</label>
                <GenericFileUpload
                    key={document?.documentId}
                    newFiles={formik.values.files}
                    onFilesChange={(files) => {
                        formik.setFieldTouched('files', true, false);
                        formik.setFieldValue('files', files ?? []);
                    }}
                    existingDocuments={existingDocuments}
                    onDownloadDocument={onDownloadDocument}
                    onDeleteExistingDocument={(existingDocument) => {
                        formik.setFieldValue('removedDocumentIds', [...formik.values.removedDocumentIds, existingDocument.id]);
                    }}
                    multiple
                    acceptedFileTypes={PDF_TYPES}
                    disabled={loading || readOnly}
                />
                {invalid('files') && <small className="p-error block">{formik.errors.files}</small>}
            </div>
        </Dialog>
    );
};

export default TrainingDocumentEditDialog;
